import { useCallback, useEffect } from 'react';
import { router } from '@inertiajs/react';
import { toast } from 'sonner';

import http from '@/lib/http';
import { developmentProsopiteWarningsPath } from '@/lib/routes';

interface ProsopiteWarningsResponse {
  warnings?: string[];
}

function toastTitle(count: number) {
  return count === 1 ? 'N+1 query detected' : `${count} N+1 queries detected`;
}

export default function ProsopiteErrorNotifier() {
  const fetchWarnings = useCallback(async () => {
    if (import.meta.env.MODE !== 'development') return;

    try {
      const response = await http.get<ProsopiteWarningsResponse>(
        developmentProsopiteWarningsPath(),
      );
      const warnings = response.data.warnings?.filter(Boolean) ?? [];
      if (warnings.length === 0) return;

      toast.error(toastTitle(warnings.length), {
        id: `prosopite-warnings`,
        duration: Infinity,
        description: (
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-red-50 p-3 text-xs text-red-950">
            {warnings.join('\n\n')}
          </pre>
        ),
      });
    } catch (error) {
      console.error('Failed to fetch Prosopite warnings:', error);
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.MODE !== 'development') return;

    void fetchWarnings();

    const removeListener = router.on('finish', () => {
      void fetchWarnings();
    });

    return () => {
      removeListener();
    };
  }, [fetchWarnings]);

  return null;
}
